/**
 * Fit a worker/template geometry (normalized 0..1 coordinates on the worker's
 * SQUARE canvas) into the play-area box so the SILHOUETTE — not the square —
 * fills the screen.
 *
 * The worker normalizes by max(width, height) of its 800×800 canvas, so a wide
 * dragon occupies a thin band (y ≈ 0.32..0.68) and, scaled naively to a square
 * canvas, used a third of the height with 10 px cells and 6 px markers. We take
 * the contour's bounding box instead, scale it uniformly to the box (minus a
 * padding), and centre it. The affine transform is kept on the data
 * (`geometryTransform`) so the renderer can place the source photo the worker
 * saw under the same mapping. Mutates `mazeData` in place, like the old scaler.
 */
import type { Position, VoronoiMazeData } from '../types/maze-types';

export interface GeometryTransform {
  /** canvas = normalized × scale + offset */
  scale: number;
  ox: number;
  oy: number;
}

export interface FittedCanvas {
  width: number;
  height: number;
}

const PAD = 56; // leaves room for the entry / exit badges (r 20, 14 px off the silhouette) outside it
const MIN_SIDE = 200;

function bboxOf(points: Position[]): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

export function fitGeometryToBox(mazeData: VoronoiMazeData, maxW: number, maxH: number, pad = PAD): FittedCanvas {
  const pts = mazeData.contour && mazeData.contour.length > 2
    ? mazeData.contour
    : mazeData.regions.flatMap((r) => r.vertices);
  const bb = bboxOf(pts) ?? { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  const bw = Math.max(1e-6, bb.maxX - bb.minX);
  const bh = Math.max(1e-6, bb.maxY - bb.minY);
  const innerW = Math.max(MIN_SIDE - 2 * pad, maxW - 2 * pad);
  const innerH = Math.max(MIN_SIDE - 2 * pad, maxH - 2 * pad);
  const scale = Math.min(innerW / bw, innerH / bh);
  const width = Math.round(bw * scale + 2 * pad);
  const height = Math.round(bh * scale + 2 * pad);
  const ox = pad - bb.minX * scale;
  const oy = pad - bb.minY * scale;
  const map = (p: Position): Position => ({ x: p.x * scale + ox, y: p.y * scale + oy });

  for (const r of mazeData.regions) {
    r.site = map(r.site);
    r.vertices = r.vertices.map(map);
  }
  if (mazeData.contour) mazeData.contour = mazeData.contour.map(map);
  mazeData.geometryTransform = { scale, ox, oy };
  return { width, height };
}

/**
 * Re-fit an already-fitted Voronoi state (cells, contour AND the drawn path) into
 * a new box — phone rotation, the idle controls collapsing, a resize. Uses the
 * recorded transform to go back to normalized space, then fits again. Returns a
 * NEW state (same mazeData identity semantics: fresh objects) or the input when
 * nothing changes by more than a pixel.
 */
export function refitVoronoiState<T extends { mazeData: VoronoiMazeData; canvasSize: { width: number; height: number }; pathSegments: { points: Position[] }[] }>(
  state: T,
  maxW: number,
  maxH: number,
  pad = PAD,
): T {
  const tf = state.mazeData.geometryTransform;
  if (!tf || !(tf.scale > 0)) return state;
  const unmap = (p: Position): Position => ({ x: (p.x - tf.ox) / tf.scale, y: (p.y - tf.oy) / tf.scale });
  const normalized: VoronoiMazeData = {
    ...state.mazeData,
    regions: state.mazeData.regions.map((r) => ({ ...r, site: unmap(r.site), vertices: r.vertices.map(unmap) })),
    contour: state.mazeData.contour?.map(unmap),
  };
  const size = fitGeometryToBox(normalized, maxW, maxH, pad);
  if (Math.abs(size.width - state.canvasSize.width) <= 1 && Math.abs(size.height - state.canvasSize.height) <= 1) return state;
  const nt = normalized.geometryTransform!;
  const remap = (p: Position): Position => {
    const n = unmap(p);
    return { x: n.x * nt.scale + nt.ox, y: n.y * nt.scale + nt.oy };
  };
  return {
    ...state,
    mazeData: normalized,
    canvasSize: size,
    pathSegments: state.pathSegments.map((s) => ({ ...s, points: s.points.map(remap) })),
  };
}
